/* eslint-disable @typescript-eslint/no-explicit-any */
import { trackEvent } from "@intercom/messenger-js-sdk";
import { useEffect, useRef, useState } from "react";
import { RiRecordCircleFill } from "react-icons/ri";
import { NavLink, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Box,
  Breadcrumbs,
  Button,
  Flex,
  Group,
  Tabs,
  Text,
  Tooltip,
  Transition,
} from "@mantine/core";
import { IconPencil, IconSparkles } from "@tabler/icons-react";
import {
  FetchUserAttributesOutput,
  GetCurrentUserOutput,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from "aws-amplify/auth";
import {
  GraphQLQuery,
  GraphQLSubscription,
  generateClient,
  post,
  put,
} from "aws-amplify/api";
import BoxLoader from "@/components/BoxLoader";
import ConfirmDeleteCaseModal from "@/components/Case/ConfirmDeleteCaseModal";
import CreateRecordModal from "@/components/Case/CreateRecordModal";
import EditCaseModal from "@/components/Case/EditCaseModal";
import PayWallModal from "@/components/Case/PayWallModal";
import TabPanelRecordings from "@/components/CaseDetail/Tabs/TabPanelRecordings";
import TabPanelSummary from "@/components/CaseDetail/Tabs/TabPanelSummary";
import TabPanelTemplate from "@/components/CaseDetail/Tabs/TabPanelTemplate";
import TabPanelTranscription from "@/components/CaseDetail/Tabs/TabPanelTranscription";
import { useUserProvider } from "@/utils/UserProvider";
import { checkIfMaxTemplates } from "@/utils/checkSubscriptionUsage";
import {
  appShowNotification,
  sortByCreatedAt,
  sortByUpdatedAt,
} from "@/utils/utils";
import { getCaseShallow } from "@/graphql/getCaseShallow";
import { getPatientShallow } from "@/graphql/getPatientShallow";
import { detailsByCID, templatesByLIDAndUpdatedAt } from "@/graphql/queries";
import {
  onCreateDetails,
  onDeleteDetails,
  onUpdateCase,
  onUpdateDetails,
} from "@/graphql/subscriptions";
import {
  DetailsByCIDQuery,
  GetCaseQuery,
  GetCaseQueryVariables,
  GetPatientQuery,
  ModelSortDirection,
  OnCreateDetailsSubscription,
  OnCreateDetailsSubscriptionVariables,
  OnDeleteDetailsSubscription,
  OnDeleteDetailsSubscriptionVariables,
  OnUpdateCaseSubscription,
  OnUpdateCaseSubscriptionVariables,
  OnUpdateDetailsSubscription,
  OnUpdateDetailsSubscriptionVariables,
  TemplatesByLIDAndUpdatedAtQuery,
  TemplatesByLIDAndUpdatedAtQueryVariables,
} from "@/API";

const client = generateClient();

const CaseDetail = () => {
  const { amplifyUser, revcatUser, mixpanel } = useUserProvider();
  const location = useLocation();
  const navigate = useNavigate();
  const patientCase = location.state;
  if (patientCase == null) {
    window.location.href = "/patients";
  }

  const [caseDetail, setCaseDetail] = useState(patientCase);
  const [caseSummaryDetail, setCaseSummaryDetail] = useState(patientCase);
  const [patient, setPatient] = useState<any>(patientCase.patient);
  const [caseTemplateDetail, setCaseTemplateDetail] = useState<any>([]);
  const [details, setDetails] = useState<any>([]);
  const [showPayWall, setShowPayWall] = useState(false);
  const summariedRef = useRef(false);

  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  async function fetchTemplates() {
    try {
      const userAttr: FetchUserAttributesOutput = await fetchUserAttributes();
      const templatesQueryInput: TemplatesByLIDAndUpdatedAtQueryVariables = {
        lID: userAttr["custom:location"] as string,
        sortDirection: ModelSortDirection.DESC,
        filter: {
          _deleted: { ne: true },
          archived: { ne: true },
        },
      };
      const templatesData: any = await client.graphql<
        GraphQLQuery<TemplatesByLIDAndUpdatedAtQuery>
      >({ query: templatesByLIDAndUpdatedAt, variables: templatesQueryInput });
      const templatesList = templatesData.data.templatesByLIDAndUpdatedAt.items;

      setTemplates(templatesList.sort(sortByUpdatedAt) as any);
    } catch (err) {
      console.warn(err);
    }
  }

  const TABS = {
    template: "template",
    summary: "summary",
    transcriptions: "transcriptions",
    recordings: "recordings",
  };

  useEffect(() => {
    setCaseDetail(patientCase);
  }, [patientCase]);

  useEffect(() => {
    if (!caseDetail?.caseTemplateId) {
      setActiveTab(TABS.transcriptions);
    } else {
      if (caseDetail?.templateData) {
        const sections = JSON.parse(caseDetail.templateData).sections;
        setCaseTemplateDetail(sections);
      }
      if (summariedRef.current) {
        setActiveTab(TABS.summary);
      } else {
        setActiveTab(TABS.template);
      }
    }
  }, [caseDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = [
    { title: patientCase?.patient?.name, href: "/patients" },
    {
      title: "Cases",
      href: `/cases/${patientCase?.patientId || patientCase?.pID}`,
      state: patientCase.patient,
    },
    {
      title: "Case Detail",
      href: "",
      isActive: true,
    },
  ].map((item, index) =>
    item.href ? (
      <NavLink
        to={item.href}
        className={item.isActive ? "active" : ""}
        key={index}
        state={item?.state}
      >
        {item.title}
      </NavLink>
    ) : (
      <span className={item.isActive ? "active" : ""} key={index}>
        {item.title}
      </span>
    ),
  );
  const [activeTab, setActiveTab] = useState<string | null | any>(
    TABS.transcriptions,
  );

  const [modalEditOpened, setModalEditOpened] = useState(false);
  const [modalCreateRecordOpened, setModalCreateRecordOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalConfirmDeleteOpened, setModalConfirmDeleteOpened] =
    useState(false);

  useEffect(() => {
    fetchDetails();
    fetchCase();
    setupSubscription();
    
    // Trigger case migration to Supabase when viewing individual case
    if (patientCase?.id) {
      console.log('[CASE-MIGRATION] Triggering migration for case:', patientCase.id);
      setTimeout(async () => {
        try {
          const { RelationshipMigrationService } = await import("@/services/RelationshipMigrationService");
          const orchestrator = RelationshipMigrationService.getInstance();
          
          // Ensure patient exists first, then migrate case with relationship awareness
          if (patient?.id) {
            console.log('[CASE-MIGRATION] Ensuring patient exists first:', patient.name);
            await orchestrator.ensurePatientExists(patient.id);
          }
          
          console.log('[CASE-MIGRATION] Migrating case with relationship awareness:', patientCase.title || patientCase.id);
          await orchestrator.migrateCaseEnsuringPatient(
            patientCase as Record<string, unknown>, 
            { data: { createCase: patientCase } } as Record<string, unknown>
          );
          console.log('[CASE-MIGRATION] Successfully migrated case to Supabase');
        } catch (error) {
          console.warn('[CASE-MIGRATION] Migration failed:', error);
        }
      }, 1500); // Start after 1.5 seconds
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setupSubscription = async () => {
    const varsCreateDetails: OnCreateDetailsSubscriptionVariables = {
      filter: {
        cID: { eq: patientCase.id },
      },
    };
    const createSub = client
      .graphql<GraphQLSubscription<OnCreateDetailsSubscription>>({
        query: onCreateDetails,
        variables: varsCreateDetails,
      })
      .subscribe({
        next: () => {
          fetchDetails();
        },
        error: (error) => console.warn(error),
      });

    const varsUpdateDetails: OnUpdateDetailsSubscriptionVariables = {
      filter: {
        cID: { eq: patientCase.id },
      },
    };
    const updateSub = client
      .graphql<GraphQLSubscription<OnUpdateDetailsSubscription>>({
        query: onUpdateDetails,
        variables: varsUpdateDetails,
      })
      .subscribe({
        next: () => {
          fetchDetails();
        },
        error: (error) => console.warn(error),
      });

    const varsDeleteDetails: OnDeleteDetailsSubscriptionVariables = {
      filter: {
        cID: { eq: patientCase.id },
      },
    };
    const deleteSub = client
      .graphql<GraphQLSubscription<OnDeleteDetailsSubscription>>({
        query: onDeleteDetails,
        variables: varsDeleteDetails,
      })
      .subscribe({
        next: () => {
          fetchDetails();
        },
        error: (error) => console.warn(error),
      });

    const varsUpdateCase: OnUpdateCaseSubscriptionVariables = {
      filter: {
        id: { eq: patientCase?.id },
      },
    };

    // Set up subscription to Cases table
    const updateCaseSub = client
      .graphql<GraphQLSubscription<OnUpdateCaseSubscription>>({
        query: onUpdateCase,
        variables: varsUpdateCase,
      })
      .subscribe({
        next: async ({ data }) => {
          if (data.onUpdateCase === null) {
            fetchCase();
          } else {
            setCaseDetail(data.onUpdateCase);
            if (data.onUpdateCase && data.onUpdateCase.templateData) {
              const sections = JSON.parse(
                data.onUpdateCase.templateData,
              ).sections;
              setCaseTemplateDetail(sections);
            } else {
              console.warn(
                "Template data or case from onUpdate is null/undefined.",
              );
            }
          }
        },
        error: (error) => console.warn(error),
      });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
      deleteSub.unsubscribe();
      updateCaseSub.unsubscribe();
    };
  };

  const fetchCase = async () => {
    const caseQueryInput: GetCaseQueryVariables = {
      id: patientCase.id,
    };
    const caseData = await client.graphql<GraphQLQuery<GetCaseQuery>>({
      query: getCaseShallow,
      variables: caseQueryInput,
    });
    setCaseDetail(caseData.data.getCase);
    if (caseData.data.getCase && caseData.data.getCase.templateData) {
      const sections = JSON.parse(caseData.data.getCase.templateData).sections;
      setCaseTemplateDetail(sections);
    } else {
      console.warn("Template data or case is null/undefined");
    }
  };

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const getPatientData = await client.graphql<
        GraphQLQuery<GetPatientQuery>
      >({
        query: getPatientShallow,
        variables: { id: patientCase.patientId || patientCase.pID },
      });
      const newPatient = getPatientData.data.getPatient;
      setPatient(newPatient);

      // Get details
      const detailData: any = await client.graphql<
        GraphQLQuery<DetailsByCIDQuery>
      >({
        query: detailsByCID,
        variables: { cID: patientCase.id },
      });

      const detailList = await detailData.data.detailsByCID.items;
      setDetails(detailList.sort(sortByCreatedAt));
      if (detailList.length > 0) {
        setCaseSummaryDetail(detailList[0].case);
      }
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  };

  const handleSummarize = async (section: any) => {
    setLoading(true);
    try {
      const init = {
        response: false,
        headers: {},
        body: {
          patientId: patientCase.patientId || patientCase.pID,
          caseId: patientCase.id,
          detailType: section,
        },
      };

      const summarizeTextResult = post({
        apiName: "SummarizeText",
        path: `/summarizetext`,
        options: init,
      });
      await summarizeTextResult.response;
      summariedRef.current = true;
      await fetchDetails();
      setTimeout(() => {
        summariedRef.current = false;
      }, 1000);
    } catch (error) {
      console.warn(error);
    }
    setLoading(false);
    // setIsDetailOptionsModalVisible(false);
    // setActiveTab("transcriptions");
  };

  const handleSummarizeCase = async () => {
    setLoading(true);
    try {
      const init = {
        response: false,
        headers: {},
        body: {
          patientId: patientCase.patientId || patientCase.pID,
          caseId: patientCase.id,
          detailType: null,
          summarizeCase: true,
        },
      };

      const summarizeTextResult = post({
        apiName: "SummarizeText",
        path: `/summarizetext`,
        options: init,
      });
      await summarizeTextResult.response;
      summariedRef.current = true;
      await fetchCase();
      setTimeout(() => {
        summariedRef.current = false;
      }, 1000);
    } catch (error) {
      console.warn(error);
    }
    setLoading(false);
  };

  const handleGenerateCaseTemplate = async () => {
    if (await checkIfMaxTemplates(revcatUser, amplifyUser)) {
      setShowPayWall(true);
      appShowNotification({
        type: "error",
        title:
          "You have reached the limit.\nPlease upgrade your subscription from your Profile",
      });
      return;
    } else {
      setLoading(true);
      try {
        const user: GetCurrentUserOutput = await getCurrentUser();
        const { identityId } = await fetchAuthSession();
        const init = {
          response: false,
          headers: {},
          body: {
            locationId: caseDetail.lID,
            templateId: caseDetail.caseTemplateId,
            username: user.username,
            userIdentityId: identityId ?? "",
          },
        };

        const generateResult = put({
          apiName: "GenerateCaseTemplate",
          path: `/generatecasetemplate/${caseDetail.id}`,
          options: init,
        });
        console.log(await generateResult.response);
        mixpanel.track("Use Template", {});
        trackEvent("generate_template");
      } catch (error) {
        console.warn(error);
        setLoading(false);
        return;
      }
      setLoading(false);
    }
  };

  const showBoxRecord = [
    TABS.template,
    TABS.transcriptions,
    TABS.recordings,
  ].includes(activeTab);

  const template = templates.find(
    (template: any) => template.id === caseDetail.caseTemplateId,
  );

  return (
    <Box p={15} pb={0}>
      <Breadcrumbs separatorMargin={3} mt={0} separator="→">
        {items}
      </Breadcrumbs>
      <Group
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex",
          width: "100%",
          maxHeight: "100%",
        }}
      >
        {/* Header */}
        <Group
          className="header-patients"
          flex={1}
          mt={10}
          align="center"
          w={"100%"}
          justify="space-between"
        >
          <Flex align={"center"}>
            {caseDetail && (
              <>
                <Flex direction={"column"}>
                  <Text fz={20} fw={"bold"} mr={5} lh={1}>
                    {caseDetail?.title || ""}
                  </Text>
                </Flex>
                <Tooltip label="Edit case" withArrow>
                  <ActionIcon
                    radius={20}
                    size={"md"}
                    p={3}
                    variant="subtle"
                    onClick={() => setModalEditOpened(true)}
                  >
                    <IconPencil color="gray" size={"lg"} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Flex>
        </Group>
        <Flex
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "start",
            alignItems: "center",
            position: "relative",
            width: "100%",
            minHeight: "calc(100vh - 300px)",
          }}
        >
          <Tabs value={activeTab} onChange={setActiveTab} w={"100%"}>
            <Tabs.List flex={1} justify="space-between">
              {caseDetail.caseTemplateId && (
                <Tabs.Tab flex={1} value={TABS.template}>
                  Template
                </Tabs.Tab>
              )}

              <Tabs.Tab flex={1} value={TABS.transcriptions}>
                Transcriptions
              </Tabs.Tab>
              <Tabs.Tab flex={1} value={TABS.summary}>
                Summary
              </Tabs.Tab>
              <Tabs.Tab flex={1} value={TABS.recordings}>
                Recordings
              </Tabs.Tab>
            </Tabs.List>

            {caseDetail.caseTemplateId && template && (
              <Tabs.Panel value={TABS.template} className="case-panel">
                <Transition
                  mounted={activeTab == TABS.template}
                  transition="fade"
                  duration={400}
                  timingFunction="ease"
                >
                  {(styles) => (
                    <TabPanelTemplate
                      styles={styles}
                      template={template}
                      caseTemplateDetail={caseTemplateDetail}
                      caseDetail={caseDetail}
                    />
                  )}
                </Transition>
              </Tabs.Panel>
            )}
            <Tabs.Panel value={TABS.transcriptions} className="case-panel">
              <Transition
                mounted={activeTab == TABS.transcriptions}
                transition="fade"
                duration={400}
                timingFunction="ease"
              >
                {(styles) => (
                  <TabPanelTranscription
                    details={details}
                    handleSummarize={handleSummarize}
                    loading={loading}
                    styles={styles}
                  />
                )}
              </Transition>
            </Tabs.Panel>
            <Tabs.Panel value={TABS.summary} className="case-panel">
              <Transition
                mounted={activeTab == TABS.summary}
                transition="fade"
                duration={400}
                timingFunction="ease"
              >
                {(styles) => (
                  <TabPanelSummary
                    styles={styles}
                    caseDetail={caseDetail}
                    details={details}
                    summarizeCase={handleSummarizeCase}
                  />
                )}
              </Transition>
            </Tabs.Panel>
            <Tabs.Panel value={TABS.recordings} className="case-panel">
              <Transition
                mounted={activeTab == TABS.recordings}
                transition="fade"
                duration={400}
                timingFunction="ease-in-out"
              >
                {(styles) => (
                  <div style={styles}>
                    <TabPanelRecordings
                      caseDetail={caseSummaryDetail}
                      details={details}
                      patientId={patientCase.patientId || patientCase.pID}
                    />
                  </div>
                )}
              </Transition>
            </Tabs.Panel>
          </Tabs>
          {showBoxRecord && (
            <Box className="box-record">
              <Flex gap={10}>
                <Button
                  className="btn-record"
                  mt={10}
                  radius={"xl"}
                  w={250}
                  h={45}
                  onClick={() => setModalCreateRecordOpened(true)}
                >
                  <Flex align={"center"}>
                    <RiRecordCircleFill />
                    <Text ml={5}>Record</Text>
                  </Flex>
                </Button>

                {caseDetail.caseTemplateId !== null &&
                  caseDetail.recordings &&
                  caseDetail.recordings.items.length > 0 && (
                    <Button
                      className="btn-generate"
                      mt={10}
                      radius={"xl"}
                      w={245}
                      bg={"#fff"}
                      variant="outline"
                      h={45}
                      onClick={handleGenerateCaseTemplate}
                    >
                      <Flex align={"center"}>
                        <IconSparkles />
                        <Text ml={5}>Generate</Text>
                      </Flex>
                    </Button>
                  )}
              </Flex>
            </Box>
          )}
          {details.length === 0 && loading ? (
            <BoxLoader className={"box-loader-detail"} />
          ) : null}
          {loading ? (
            <BoxLoader className={"box-loader-detail bg-opacity-60"} />
          ) : null}
        </Flex>
        <EditCaseModal
          modalOpened={modalEditOpened}
          setModalOpened={setModalEditOpened}
          patientCase={caseDetail}
          fetchCases={() => { }}
          templates={templates}
          setPatientCase={setCaseDetail}
          setModalConfirmDeleteOpened={setModalConfirmDeleteOpened}
        />
        <CreateRecordModal
          modalOpened={modalCreateRecordOpened}
          setModalOpened={setModalCreateRecordOpened}
          recordType={""}
          useTemplate={
            caseDetail.caseTemplateId != null &&
            caseDetail.caseTemplateId != "" &&
            activeTab == TABS.template
          }
          caseInfo={caseDetail}
          patientInfo={patient}
          isShowSelectType={activeTab == TABS.transcriptions}
          fetchDetails={fetchDetails}
        />
        <ConfirmDeleteCaseModal
          modalOpened={modalConfirmDeleteOpened}
          setModalOpened={setModalConfirmDeleteOpened}
          patientCase={caseDetail}
          fetchCases={() => {
            navigate(`/cases/${patient.id}`, { state: patient });
          }}
        />
        {amplifyUser && (
          <PayWallModal
            modalOpened={showPayWall}
            setModalOpened={setShowPayWall}
          />
        )}
      </Group>
    </Box>
  );
};

export default CaseDetail;
